import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Switch,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import QuizIcon from '@mui/icons-material/Quiz';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { RootState } from '../../../../store';
import {
  useGetDataAgentExampleQueriesQuery,
  useUpdateDataAgentExampleQueryMutation,
} from '../../../../api/dataAgentApiSlice';
import { CustomConfirmDialog, showToaster } from '../../../../utils/globalImports';
import AddEditDataAgentExampleQueryUIForm from './AddEditDataAgentExampleQueryUIForm';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface DataAgentExampleQueriesTabProps {
  dataAgentId: string;
}

const DataAgentExampleQueriesTab: React.FC<DataAgentExampleQueriesTabProps> = ({ dataAgentId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selectedCustomerId: customerId, selectedClientId: clientId } = useSelector(
    (state: RootState) => state.vaultilake,
  );

  const [isAddQueryOpen, setIsAddQueryOpen] = useState(false);
  const [editingQuery, setEditingQuery] = useState<any>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // State for confirmation dialog
  const [dialog, setDialog] = useState<{
    open: boolean;
    query: any | null;
    newStatus: boolean | null;
  }>({
    open: false,
    query: null,
    newStatus: null,
  });

  const { data, isLoading, isError, error } = useGetDataAgentExampleQueriesQuery({
    data_agent_id: dataAgentId,
    customer_id: customerId || '',
    client_id: clientId || '',
  });

  // Use update mutation instead of enable/disable
  const [updateExampleQuery] = useUpdateDataAgentExampleQueryMutation();

  const exampleQueries = useMemo(() => {
    if (isError && (error as any)?.status === 404) {
      return [];
    }
    return data ?? [];
  }, [data, isError, error]);

  const hasRealError = isError && (error as any)?.status !== 404;

  const handleAddQuery = () => {
    setEditingQuery(null);
    setIsAddQueryOpen(true);
  };

  const handleCloseAddQuery = () => {
    setIsAddQueryOpen(false);
    setEditingQuery(null);
    setIsFullscreen(false); // Reset fullscreen when closing
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleEdit = (query: any) => {
    // Pass the entire query object with data_agent_example_query_id
    setEditingQuery(query);
    setIsAddQueryOpen(true);
  };

  // Handle status toggle initiation
  const handleToggleStatus = (query: any) => {
    const newStatus = !query.is_active;
    setDialog({
      open: true,
      query,
      newStatus,
    });
  };

  // Handle status update after confirmation
  const handleConfirmStatusUpdate = async () => {
    if (!dialog.query || dialog.newStatus === null) return;

    try {
      const updatePayload = {
        data_agent_example_query_id: dialog.query.data_agent_example_query_id,
        data_agent_id: dataAgentId,
        customer_id: customerId || '',
        client_id: clientId || '',
        question: dialog.query.question,
        query: dialog.query.query,
        query_language: dialog.query.query_language,
        description: dialog.query.description,
        expected_result: dialog.query.expected_result,
        is_active: dialog.newStatus,
      };

      await updateExampleQuery(updatePayload).unwrap();

      dispatch(
        showToaster({
          message: dialog.newStatus
            ? t('dataAgents.exampleQueries.messages.queryActivated')
            : t('dataAgents.exampleQueries.messages.queryDeactivated'),
          type: 'success',
        }),
      );
    } catch (err: any) {
      dispatch(
        showToaster({
          message: err.data?.message || t('dataAgents.exampleQueries.messages.errorUpdatingStatus'),
          type: 'error',
        }),
      );
    } finally {
      setDialog({ open: false, query: null, newStatus: null });
    }
  };

  // Handle dialog cancel/close
  const handleDialogClose = () => {
    setDialog({ open: false, query: null, newStatus: null });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (hasRealError) {
    return (
      <Box p={2}>
        <Typography color="error">
          {t('dataAgents.exampleQueries.errorLoadingQueries')}:{' '}
          {(error as any)?.data?.message || t('dataAgents.dataAgentSources.errors.unknownError')}
        </Typography>
      </Box>
    );
  }

  // Empty State Component
  const EmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 2,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'grey.50',
      }}
    >
      <QuizIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
      <Typography variant="body2" color="text.secondary" align="center" mb={0.5}>
        {t('dataAgents.exampleQueries.noQueriesConfigured')}
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Main Container with Flexbox Layout */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%', // Changed from maxHeight: '600px'
          minHeight: 0, // Important for flex overflow
        }}
      >
        {/* Fixed Header Section */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t('dataAgents.exampleQueries.title')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddQuery}
            sx={{ textTransform: 'none' }}
          >
            {t('dataAgents.exampleQueries.addExample')}
          </Button>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 0.5,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: '#555',
              },
            },
          }}
        >
          {/* Example Queries List */}
          {exampleQueries.length > 0 ? (
            exampleQueries.map((query: any, index: number) => (
              <Box
                key={query.data_agent_example_query_id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: 'background.paper',
                  p: 1.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {/* Compact Header Row */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        backgroundColor: 'primary.light',
                        color: '#FFFFFF',
                        borderRadius: '4px',
                        px: 1,
                        py: 0.25,
                        fontSize: '11px',
                        fontWeight: 600,
                      }}
                    >
                      {t('dataAgents.exampleQueries.exampleLabel', { number: index + 1 })}
                    </Box>
                  </Box>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <Tooltip title={t('dataAgents.exampleQueries.editQuery')}>
                      <IconButton size="small" onClick={() => handleEdit(query)} sx={{ color: '#666' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        query.is_active
                          ? t('dataAgents.exampleQueries.disableQuery')
                          : t('dataAgents.exampleQueries.enableQuery')
                      }
                    >
                      <Switch
                        checked={query.is_active}
                        onChange={() => handleToggleStatus(query)}
                        color="primary"
                        size="small"
                      />
                    </Tooltip>
                  </Box>
                </Box>

                {/* Compact Content */}
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="text.secondary"
                    sx={{ fontSize: '11px', mb: 0.5 }}
                  >
                    {t('dataAgents.exampleQueries.questionLabel')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px', mb: 1.5 }}>
                    {query.question}
                  </Typography>

                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="text.secondary"
                    sx={{ fontSize: '11px', mb: 0.5 }}
                  >
                    {t('dataAgents.exampleQueries.answerLabel', { language: query.query_language || 'SQL' })}
                  </Typography>

                  {/* Wrapper Box for Copy Button and Query Display */}
                  <Box sx={{ position: 'relative' }}>
                    <Tooltip title={t('common.copy') || 'Copy query'}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          navigator.clipboard.writeText(query.query);
                          dispatch(
                            showToaster({
                              message: t('common.copiedToClipboard') || 'Query copied to clipboard',
                              type: 'success',
                            }),
                          );
                        }}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          zIndex: 1,
                          padding: '4px',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            color: 'primary.main',
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: '14px' }} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip
                      title={query.query.length > 100 ? query.query : ''}
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            maxWidth: '1200px',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            backgroundColor: '#1e1e1e',
                            color: '#d4d4d4',
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            padding: '12px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: '#1e1e1e',
                          color: '#d4d4d4',
                          p: 1.5,
                          borderRadius: 1,
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          overflowX: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          cursor: query.query.length > 100 ? 'help' : 'default',
                        }}
                      >
                        {query.query.length > 100 ? `${query.query.substring(0, 300)}...` : query.query}
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <EmptyState />
          )}
        </Box>
      </Box>

      {/* Add/Edit Query Dialog with Fullscreen Support */}
      <Dialog
        open={isAddQueryOpen}
        onClose={handleCloseAddQuery}
        fullScreen={isFullscreen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: isFullscreen ? '100vw' : '80vw',
            maxWidth: isFullscreen ? '100vw' : '900px',
            height: isFullscreen ? '100vh' : '90vh',
            maxHeight: isFullscreen ? '100vh' : '90vh',
            display: 'flex',
            flexDirection: 'column',
            m: isFullscreen ? 0 : 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">
            {editingQuery
              ? t('dataAgents.exampleQueries.editExampleQuery')
              : t('dataAgents.exampleQueries.addExampleQuery')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={isFullscreen ? t('common.exitFullscreen') : t('common.enterFullscreen')}>
              <IconButton
                edge="end"
                color="inherit"
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                sx={{ color: 'text.secondary' }}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            <IconButton edge="end" color="inherit" onClick={handleCloseAddQuery} aria-label={t('common.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2, flex: 1, overflow: 'auto' }}>
          <AddEditDataAgentExampleQueryUIForm
            onCancel={handleCloseAddQuery}
            initialData={editingQuery}
            dataAgentId={dataAgentId}
          />
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <CustomConfirmDialog
        open={dialog.open}
        title={t('dataAgents.exampleQueries.confirmDialog.title')}
        message={
          dialog.newStatus === true
            ? t('dataAgents.exampleQueries.confirmDialog.activateMessage')
            : t('dataAgents.exampleQueries.confirmDialog.deactivateMessage')
        }
        onConfirm={handleConfirmStatusUpdate}
        onCancel={handleDialogClose}
        onClose={handleDialogClose}
        confirmLabel={t('dataAgents.exampleQueries.confirmDialog.confirm')}
        cancelLabel={t('dataAgents.exampleQueries.confirmDialog.cancel')}
      />
    </>
  );
};

export default DataAgentExampleQueriesTab;
